import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import {
  dismissAnnouncement,
  getActiveAnnouncements,
  type ActiveAnnouncement,
} from '../../shared/api/announcements.api'
import { useAuthStore } from '../auth/authStore'
import { toast } from '../../shared/ui/toastStore'

/**
 * Активные объявления текущего сотрудника — для плашки под шапкой и окна
 * при входе.
 *
 * Один запрос на оба места показа: react-query дедуплицирует по ключу, и
 * плашка с окном берут данные из одного ответа. Ключ — с id пользователя,
 * чтобы после смены аккаунта в той же вкладке не показать чужой список.
 */
export const announcementsKey = (userId: string) => ['announcements', 'active', userId] as const

const errText = (e: unknown): string => {
  if (axios.isAxiosError(e)) {
    const msg = (e.response?.data as { error?: { message?: unknown } } | undefined)?.error?.message
    if (typeof msg === 'string' && msg) return msg
  }
  return 'Не удалось закрыть объявление'
}

export function useActiveAnnouncements() {
  const queryClient = useQueryClient()
  const userId = useAuthStore((s) => s.user?.id) ?? ''
  const key = announcementsKey(userId)

  const { data } = useQuery({
    queryKey: key,
    queryFn: getActiveAnnouncements,
    enabled: userId !== '',
    // Запрос уходит, когда монтируется общий каркас приложения: при входе в
    // аккаунт и при перезагрузке страницы (переходы между разделами каркас не
    // пересоздают). staleTime 0 — новый вход того же человека без перезагрузки
    // получает свежий список: объявление «каждый вход» должно вернуться.
    staleTime: 0,
  })

  const { mutate, isPending } = useMutation({
    mutationFn: (id: string) => dismissAnnouncement(id),
    // Убираем сразу, не дожидаясь ответа: нажали «Понятно» — сообщение ушло.
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: key })
      const before = queryClient.getQueryData<ActiveAnnouncement[]>(key)
      queryClient.setQueryData<ActiveAnnouncement[]>(key, (list) =>
        (list ?? []).filter((a) => a.id !== id),
      )
      return { before }
    },
    onError: (e, _id, ctx) => {
      if (ctx?.before) queryClient.setQueryData(key, ctx.before)
      toast.error(errText(e))
    },
  })

  const dismiss = useCallback((id: string) => mutate(id), [mutate])
  const items = data ?? []
  return {
    banners: items.filter((a) => a.display === 'BANNER'),
    modals: items.filter((a) => a.display === 'MODAL'),
    dismiss,
    dismissing: isPending,
  }
}
